import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { PRODUCT_SOURCE, shopifyCheckout } from "../lib/products";
import { Lines, FadeUp } from "../components/Reveal";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    ["email", "Email Address", "email", "you@example.com"],
    ["name", "Full Name", "text", "Full name"],
    ["address", "Address", "text", "Street address"],
    ["city", "City", "text", "City"],
    ["postal", "Postal Code", "text", "Postal code"],
];

export default function CheckoutPage() {
    const { items, subtotal, itemKey } = useStore();
    const [form, setForm] = useState({ email: "", name: "", address: "", city: "", postal: "", country: "Canada" });
    const [placing, setPlacing] = useState(false);
    const navigate = useNavigate();

    // Shipping is never added here — Shopify checkout calculates it from the store's shipping settings
    const shipping = 0;
    const total = subtotal + shipping;

    const placeOrder = async (e) => {
        e.preventDefault();
        setPlacing(true);
        try {
            if (PRODUCT_SOURCE === "shopify") {
                const url = await shopifyCheckout(items);
                window.location.href = url;
                return;
            }
            const r = await axios.post(`${API}/payments/cart-checkout`, {
                ...form,
                origin_url: window.location.origin,
                items: items.map((i) => ({
                    product_id: i.product_id,
                    size: i.size,
                    color: i.color,
                    print_type: i.print_type,
                    qty: i.qty,
                })),
            });
            window.location.href = r.data.checkout_url;
        } catch {
            toast.error("Could not start checkout — try again");
            setPlacing(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6">
                <p data-testid="checkout-empty" className="text-xs uppercase tracking-[0.3em] text-[#6B6A66]">
                    Your bag is empty
                </p>
                <Link to="/shop" className="link-underline font-display text-[11px] font-bold uppercase tracking-[0.3em]">
                    Back to shop
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page" className="pt-32">
            <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-16">
                <Link
                    data-testid="checkout-back"
                    to="/shop"
                    className="mb-10 inline-flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-[#6B6A66] transition-colors hover:text-[#0A0A0A]"
                >
                    <ArrowLeft size={12} /> Continue Shopping
                </Link>
                <Lines
                    lines={["Checkout"]}
                    className="font-display text-4xl font-black uppercase tracking-tighter text-[#0A0A0A] sm:text-6xl"
                />
                <div className="mt-14 grid grid-cols-1 gap-16 pb-24 lg:grid-cols-2">
                    <FadeUp>
                        <form data-testid="checkout-form" onSubmit={placeOrder}>
                            <p className="mb-6 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">Shipping Details</p>
                            <div className="space-y-6">
                                {FIELDS.map(([key, label, type, ph]) => (
                                    <input
                                        key={key}
                                        data-testid={`checkout-input-${key}`}
                                        type={type}
                                        required
                                        value={form[key]}
                                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                        placeholder={label.toUpperCase()}
                                        className="voc-input uppercase"
                                    />
                                ))}
                                <input
                                    data-testid="checkout-input-country"
                                    value={form.country}
                                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                                    placeholder="COUNTRY"
                                    className="voc-input uppercase"
                                />
                            </div>
                            <p className="mt-10 text-[9px] uppercase leading-loose tracking-[0.25em] text-[#6B6A66]">
                                {PRODUCT_SOURCE === "shopify"
                                    ? "Secure checkout by Shopify — cards, Apple Pay, Shop Pay and more."
                                    : "Secure checkout by Stripe — cards, Apple Pay and more. Test mode."}
                            </p>
                            <button
                                data-testid="place-order-button"
                                type="submit"
                                disabled={placing}
                                className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#0A0A0A] py-4 font-display text-[11px] font-bold uppercase tracking-[0.35em] text-[#F2F0EA] transition-colors hover:bg-white disabled:opacity-50"
                            >
                                {placing ? "Redirecting To Payment" : `Pay ${total} CAD`}
                                <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                            </button>
                        </form>
                    </FadeUp>
                    <FadeUp delay={0.15}>
                        <div className="border border-[rgba(10,10,10,0.12)] p-8">
                            <p className="mb-6 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">Order Summary</p>
                            <ul className="space-y-5">
                                {items.map((i) => (
                                    <li key={itemKey(i)} className="flex items-center gap-4">
                                        <img src={i.image} alt={i.name} className="h-20 w-[64px] object-cover" />
                                        <div className="flex-1">
                                            <p className="font-display text-[10px] font-bold uppercase tracking-[0.15em]">{i.name}</p>
                                            <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-[#8E8D8A]">
                                                {i.color} / {i.size} / Qty {i.qty}
                                                {i.print_type === "RAISED" && " / Raised Reality"}
                                            </p>
                                        </div>
                                        <span className="text-xs tracking-wider">${i.price * i.qty}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 space-y-3 border-t border-[rgba(10,10,10,0.12)] pt-6 text-[10px] uppercase tracking-[0.25em]">
                                <div className="flex justify-between text-[#8E8D8A]">
                                    <span>Subtotal</span>
                                    <span data-testid="checkout-subtotal">${subtotal} CAD</span>
                                </div>
                                <div className="flex justify-between text-[#8E8D8A]">
                                    <span>Shipping</span>
                                    <span className="text-[#8E8D8A]">Calculated at checkout</span>
                                </div>
                                <div className="flex justify-between border-t border-[rgba(10,10,10,0.12)] pt-4 text-xs text-[#0A0A0A]">
                                    <span>Total</span>
                                    <span data-testid="checkout-total" className="font-display font-bold">${total} CAD</span>
                                </div>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </div>
            <Footer />
        </div>
    );
}
